import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Share2, Maximize2, ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { api, errMsg } from '../api';
import { PageHeader, Card, Empty, Spinner, ghostBtn } from '../ui';
import AmlNodePanel, { AML_COLORS, AML_LABELS } from '../aml/AmlNodePanel';
import GraphCanvas from '../graph/GraphCanvas';
import GraphColorLegend from '../graph/GraphColorLegend';
import { useSecondaryFilter, SecondaryToggle } from '../graph/SecondaryFilter';
import { GraphStyleSelect, GroupPanel, useGraphStyle } from '../graph/GraphStyle';

export default function AmlGraphPage() {
  const { id } = useParams();
  const fg = useRef(null);
  const [data, setData] = useState(null);
  const [selected, setSelected] = useState(null);
  const [groups, setGroups] = useState([]);
  const [graphStyle, setGraphStyle] = useGraphStyle();

  useEffect(() => {
    api.get(`/aml/checks/${id}/graph`).then((r) => setData({ ...r.data, links: r.data.edges }))
      .catch((e) => toast.error(errMsg(e)));
  }, [id]);

  const view = useSecondaryFilter(data?.nodes || [], data?.links || [], data?.root);
  const total = useMemo(() => (data ? Object.values(data.stats).reduce((a, b) => a + b, 0) : 0), [data]);
  if (!data) return <Spinner label="Costruzione del grafo AML…" />;
  const pick = (nid) => setSelected(data.nodes.find((n) => n.id === nid) || groups.find((g) => g.id === nid) || null);

  return (
    <div>
      <PageHeader kicker="Reputational check · grafo" title="Grafo delle relazioni AML">
        <Link to={`/portal/reputational/${id}`} className={`${ghostBtn} h-10 px-4 flex items-center gap-2 text-sm`} data-testid="aml-graph-back"><ArrowLeft size={15} />Torna al check</Link>
        <SecondaryToggle filter={view} />
        <GraphStyleSelect value={graphStyle} onChange={setGraphStyle} />
        <Button onClick={() => fg.current?.fit()} className={`${ghostBtn} h-10`} data-testid="aml-graph-fit"><Maximize2 size={15} className="mr-2" />Adatta</Button>
      </PageHeader>
      <div className="flex flex-wrap gap-2 mb-4 -mt-2" data-testid="aml-graph-stats">
        {Object.entries(AML_LABELS).map(([k, l]) => (
          <span key={k} className="text-xs px-3 py-1.5 border border-[#1F242C] rounded-sm text-[#A3A3A3] flex items-center gap-2" data-testid={`aml-graph-stat-${k}`}>
            <span className="w-3 h-0.5" style={{ background: AML_COLORS[k] }} />{l}: <b className="text-[#E5E5E5]">{data.stats[k] || 0}</b>
          </span>
        ))}
        {data.from_report && <span className="text-xs px-3 py-1.5 text-[#7A7F87]">Unito con soci e dirigenti del report</span>}
      </div>
      <Card className="relative h-[72vh] min-h-[480px] overflow-hidden">
        <div className="absolute inset-0" data-testid="aml-graph-canvas">
          <GraphCanvas ref={fg} root={data.root} nodes={view.nodes} links={view.links} styleId={graphStyle} onSelect={pick} onGroups={setGroups} />
        </div>
        {total === 0 && (
          <div className="absolute inset-x-0 top-6 flex justify-center pointer-events-none">
            <Empty icon={Share2} title="Nessun collegamento AML" text="Per i soggetti verificati non risultano familiari o soggetti collegati, cariche PIL o corrispondenze Offshore Leaks." />
          </div>
        )}
        {selected && (selected.members ? <GroupPanel group={selected} onClose={() => setSelected(null)} />
          : <AmlNodePanel node={selected} graph={{ nodes: data.nodes, links: data.links }} onClose={() => setSelected(null)} />)}
      </Card>
      <GraphColorLegend aml={Object.entries(AML_LABELS).map(([k, l]) => [AML_COLORS[k], l])} />
      <p className="text-xs text-[#5A5F67] mt-3" data-testid="aml-graph-legend">Trascina per spostare, rotella per ingrandire, clicca un soggetto per i dettagli. Colori: <span style={{ color: AML_COLORS.associate }}>familiari e collegati</span> · <span style={{ color: AML_COLORS.pil }}>cariche PIL</span> · <span style={{ color: AML_COLORS.offshore }}>Offshore Leaks</span>.</p>
      {data.truncated > 0 && <p className="text-xs text-[#5A5F67] mt-1" data-testid="aml-graph-truncated">Mostrati al massimo 12 collegamenti per tipo e soggetto: altri {data.truncated} sono nel dettaglio del check.</p>}
      <p className="text-xs text-[#5A5F67] mt-1">I collegamenti derivano da corrispondenze di nome nelle fonti pubbliche: solo il richiedente può accertare l'identità del soggetto.</p>
    </div>
  );
}
